import React, { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit2, Trash2, Eye, EyeOff, Star, X, ExternalLink } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

interface CommunityPost {
  id: string;
  title: string;
  content: string;
  category: 'opportunities' | 'news' | 'events' | 'discussions';
  author_type: 'admin' | 'client' | 'consultant';
  author_name: string;
  link_url: string | null;
  image_url: string | null;
  is_featured: boolean;
  is_active: boolean;
  view_count: number;
  created_at: string;
}

const CATEGORY_LABELS = {
  opportunities: 'Opportunities',
  news: 'News',
  events: 'Events',
  discussions: 'Discussions'
};

export default function WorkdayCommunityManager() {
  const { user } = useAuth();
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState<CommunityPost | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'opportunities' as const,
    link_url: '',
    image_url: '',
    is_featured: false,
    is_active: true
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('workday_community_posts')
        .select('*')
        .order('is_featured', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      if (editingPost) {
        const { error } = await supabase
          .from('workday_community_posts')
          .update({
            ...formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingPost.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('workday_community_posts')
          .insert({
            ...formData,
            author_type: 'admin',
            author_id: user.id,
            author_name: 'Perplexity Admin'
          });

        if (error) throw error;
      }

      setShowModal(false);
      setEditingPost(null);
      setFormData({
        title: '',
        content: '',
        category: 'opportunities',
        link_url: '',
        image_url: '',
        is_featured: false,
        is_active: true
      });
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Failed to save post');
    }
  };

  const handleEdit = (post: CommunityPost) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      category: post.category,
      link_url: post.link_url || '',
      image_url: post.image_url || '',
      is_featured: post.is_featured,
      is_active: post.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    try {
      const { error } = await supabase
        .from('workday_community_posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post');
    }
  };

  const toggleActive = async (post: CommunityPost) => {
    try {
      const { error } = await supabase
        .from('workday_community_posts')
        .update({ is_active: !post.is_active })
        .eq('id', post.id);

      if (error) throw error;
      fetchPosts();
    } catch (error) {
      console.error('Error toggling active status:', error);
    }
  };

  const toggleFeatured = async (post: CommunityPost) => {
    try {
      const { error } = await supabase
        .from('workday_community_posts')
        .update({ is_featured: !post.is_featured })
        .eq('id', post.id);

      if (error) throw error;
      fetchPosts();
    } catch (error) {
      console.error('Error toggling featured status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Workday Community</h2>
          <p className="text-gray-500 mt-1">Manage community posts, news, and opportunities</p>
        </div>
        <button
          onClick={() => {
            setEditingPost(null);
            setFormData({
              title: '',
              content: '',
              category: 'opportunities',
              link_url: '',
              image_url: '',
              is_featured: false,
              is_active: true
            });
            setShowModal(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-[#40E0D0] text-white rounded-lg hover:bg-[#35c5b5] transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Create Post</span>
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {posts.map((post) => (
          <div
            key={post.id}
            className={`bg-white rounded-lg p-6 border shadow-sm ${
              post.is_active ? 'border-gray-200' : 'border-red-200 bg-red-50'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2 flex-wrap gap-1">
                  {post.is_featured && (
                    <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  )}
                  <span className="px-2 py-1 rounded text-xs font-semibold bg-[#40E0D0]/15 text-[#2ab5a5]">
                    {CATEGORY_LABELS[post.category]}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    post.author_type === 'admin'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {post.author_name}
                  </span>
                  {!post.is_active && (
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-600">
                      Inactive
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{post.title}</h3>
                <p className="text-gray-600 mb-3">{post.content}</p>
                {post.link_url && (
                  <a
                    href={post.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-[#40E0D0] hover:text-[#35c5b5] text-sm font-medium"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    View Link
                  </a>
                )}
                <div className="mt-3 text-xs text-gray-400">
                  {post.view_count} views · {new Date(post.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center space-x-1 ml-4">
                <button
                  onClick={() => toggleFeatured(post)}
                  className={`p-2 rounded transition-colors ${
                    post.is_featured
                      ? 'text-yellow-500 hover:bg-yellow-50'
                      : 'text-gray-400 hover:bg-gray-100'
                  }`}
                  title={post.is_featured ? 'Remove from featured' : 'Mark as featured'}
                >
                  <Star className={`w-5 h-5 ${post.is_featured ? 'fill-yellow-500' : ''}`} />
                </button>
                <button
                  onClick={() => toggleActive(post)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  title={post.is_active ? 'Deactivate' : 'Activate'}
                >
                  {post.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => handleEdit(post)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  title="Edit"
                >
                  <Edit2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(post.id)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {posts.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-500">No community posts yet. Create your first post!</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl font-bold text-gray-900">
                {editingPost ? 'Edit Post' : 'Create New Post'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingPost(null);
                }}
                className="text-gray-400 hover:text-gray-700 p-1 rounded hover:bg-gray-100 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  required
                >
                  <option value="opportunities">Opportunities</option>
                  <option value="news">News</option>
                  <option value="events">Events</option>
                  <option value="discussions">Discussions</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Content
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent resize-none"
                  rows={4}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Link URL (Optional)
                </label>
                <input
                  type="url"
                  value={formData.link_url}
                  onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Image URL (Optional)
                </label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-[#40E0D0] focus:ring-[#40E0D0]"
                  />
                  <span className="text-gray-700">Featured Post</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-[#40E0D0] focus:ring-[#40E0D0]"
                  />
                  <span className="text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingPost(null);
                  }}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-[#40E0D0] text-white rounded-lg hover:bg-[#35c5b5] transition-colors font-medium"
                >
                  {editingPost ? 'Update Post' : 'Create Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
